// The confirm dialog every broker governance mutation goes through
// (SM-716): approve / pause / resume / begin-close / close / emergency
// disable, and the staff invite / suspend / resume / remove / re-role.
//
// Same chrome as ConfirmModal and FormModal (modal-back / modal /
// modal-body, portalled through OverlayPortal) but with two things those
// cannot carry: a refusal that is more than a line of text — a
// BROKER_CLOSE_BLOCKED lists every obligation with its currency, amount
// and what clears it — and a result view, so the operator sees the audit
// reference broker-service returned before the dialog goes away.
//
// Submission is single-flight like FormModal (SM-347): a ref latch drops
// a second submit delivered inside the same task, and `loading` disables
// the button from the next commit on. A retry after a failure re-sends
// the SAME request — the caller keeps its body (row version and all) for
// the dialog's lifetime rather than rebuilding it per attempt.
import { useEffect, useRef } from "react";

import { Button } from "@/components/ui/Button";
import { OverlayPortal } from "@/components/ui/OverlayPortal";
import { cn } from "@/lib/cn";

export interface ActionDialogProps {
  open: boolean;
  title: string;
  description?: React.ReactNode;
  tone?: "primary" | "danger";
  confirmLabel: string;
  canSubmit: boolean;
  loading: boolean;
  onSubmit: () => void;
  onCancel: () => void;
  /** The form fields (reason, role, user id…). */
  children?: React.ReactNode;
  /** A refusal or failure, rendered under the fields. */
  error?: React.ReactNode;
  /** Set once the action succeeded: replaces the form with this view and one Done button. */
  result?: React.ReactNode;
  /** The heading once `result` is set: the question in `title` has been answered. */
  successMessage?: string;
  /** A primary action beside Done once `result` is set, e.g. open what was created (SM-963). */
  resultAction?: { label: string; onClick: () => void };
}

export function ActionDialog({
  open,
  title,
  description,
  tone = "primary",
  confirmLabel,
  canSubmit,
  loading,
  onSubmit,
  onCancel,
  children,
  error,
  result,
  successMessage,
  resultAction,
}: ActionDialogProps) {
  const submitting = useRef(false);
  useEffect(() => {
    if (!loading) submitting.current = false;
  });
  const done = result !== undefined && result !== null;

  const submit = () => {
    if (done || !canSubmit || loading || submitting.current) return;
    submitting.current = true;
    try {
      onSubmit();
    } catch (e) {
      submitting.current = false;
      throw e;
    }
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (loading) return;
      if (e.key === "Escape") {
        e.preventDefault();
        onCancel();
        return;
      }
      if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        if (done) onCancel();
        else submit();
      }
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
    // submit closes over the same props this effect already depends on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, loading, canSubmit, done, onCancel, onSubmit]);

  if (!open) return null;

  return (
    <OverlayPortal>
      <div
        className="modal-back"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={loading ? undefined : onCancel}
      >
        <div className="modal" onClick={(e) => e.stopPropagation()}>
          <div className={cn("modal-glow", tone === "danger" && !done && "modal-glow-no")} aria-hidden />
          <div className="modal-body">
            <h2 className="text-base font-semibold text-t-1">{done && successMessage ? successMessage : title}</h2>
            {done ? (
              <div className="mt-3 space-y-3 text-xs leading-relaxed text-t-2">{result}</div>
            ) : (
              <>
                {description ? <div className="mt-2 text-xs leading-relaxed text-muted">{description}</div> : null}
                <form
                  className="mt-4 space-y-3"
                  onSubmit={(e) => {
                    e.preventDefault();
                    submit();
                  }}
                >
                  {children}
                </form>
                {error ? <div className="mt-3">{error}</div> : null}
              </>
            )}
            <div className="mt-5 flex justify-end gap-2">
              {done ? (
                <>
                  <Button variant={resultAction ? "ghost" : "primary"} onClick={onCancel}>
                    Done
                  </Button>
                  {resultAction ? (
                    <Button variant="primary" onClick={resultAction.onClick}>
                      {resultAction.label}
                    </Button>
                  ) : null}
                </>
              ) : (
                <>
                  <Button variant="ghost" onClick={onCancel} disabled={loading}>
                    Cancel
                  </Button>
                  <Button
                    variant={tone === "danger" ? "danger" : "primary"}
                    type="button"
                    onClick={submit}
                    loading={loading}
                    disabled={!canSubmit || loading}
                  >
                    {confirmLabel}
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </OverlayPortal>
  );
}
